import { useState, useEffect } from 'react';
import EstimateCard from './components/EstimateCard';

const EDGE_FUNCTION_URL = 'https://sksgkyjrthinowaagdkq.supabase.co/functions/v1/get-estimates';

interface Estimate {
  id: string;
  created_at: string;
  full_name: string;
  phone: string;
  email: string;
  vehicle: string;
  insurance: string;
  message: string;
  status: string;
  signed_photo_urls: string[];
}

export default function AdminPage() {
  const [password, setPassword] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [authError, setAuthError] = useState('');
  const [estimates, setEstimates] = useState<Estimate[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [filter, setFilter] = useState<'all' | 'pending' | 'reviewed' | 'contacted'>('all');
  const [savedPassword, setSavedPassword] = useState('');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError('');
    setLoading(true);

    try {
      const res = await fetch(EDGE_FUNCTION_URL, {
        headers: { 'x-admin-password': password },
      });

      if (res.status === 401) {
        setAuthError('Incorrect password. Please try again.');
        setLoading(false);
        return;
      }

      const data = await res.json();
      if (data.error) throw new Error(data.error);

      setSavedPassword(password);
      setEstimates(data.estimates || []);
      setAuthenticated(true);
    } catch {
      setAuthError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchEstimates = async () => {
    setLoading(true);
    setFetchError('');
    try {
      const res = await fetch(EDGE_FUNCTION_URL, {
        headers: { 'x-admin-password': savedPassword },
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setEstimates(data.estimates || []);
    } catch {
      setFetchError('Failed to refresh estimates.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authenticated) {
      const interval = setInterval(fetchEstimates, 60000);
      return () => clearInterval(interval);
    }
  }, [authenticated, savedPassword]);

  const filtered = filter === 'all' ? estimates : estimates.filter((e) => e.status === filter);

  const counts = {
    all: estimates.length,
    pending: estimates.filter((e) => e.status === 'pending').length,
    reviewed: estimates.filter((e) => e.status === 'reviewed').length,
    contacted: estimates.filter((e) => e.status === 'contacted').length,
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-4">
        <div className="w-full max-w-sm">
          <div className="text-center mb-10">
            <div className="w-16 h-16 flex items-center justify-center rounded-full border border-yellow-700/40 bg-yellow-900/10 mx-auto mb-5">
              <i className="ri-lock-line text-2xl text-yellow-500"></i>
            </div>
            <h1 className="font-serif text-2xl text-white mb-2">Admin Panel</h1>
            <p className="text-white/40 text-sm">The Royal Collision Centre</p>
          </div>

          <form onSubmit={handleLogin} className="bg-[#111111] border border-yellow-700/20 rounded-2xl p-8 space-y-5">
            <div>
              <label className="text-yellow-600/70 text-xs tracking-widest uppercase mb-2 block">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Enter admin password"
                className="w-full bg-[#1a1a1a] border border-yellow-700/20 rounded-lg px-4 py-3 text-sm text-white placeholder-white/20 focus:outline-none focus:border-yellow-600/60 transition-colors duration-300"
              />
            </div>

            {authError && (
              <div className="bg-red-900/20 border border-red-700/40 rounded-lg px-4 py-3 text-red-400 text-xs">
                {authError}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="whitespace-nowrap w-full flex items-center justify-center gap-2 py-3 text-sm tracking-widest uppercase text-yellow-300 bg-gradient-to-r from-yellow-900/40 via-yellow-800/20 to-black border border-yellow-600/40 rounded-lg hover:border-yellow-500 transition-all duration-300 cursor-pointer disabled:opacity-50"
            >
              {loading ? <><i className="ri-loader-4-line animate-spin"></i> Verifying...</> : <><i className="ri-login-box-line"></i> Enter</>}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Top Bar */}
      <div className="bg-[#0c0c0c] border-b border-yellow-700/20 px-6 md:px-10 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center">
            <i className="ri-tools-line text-yellow-500 text-lg"></i>
          </div>
          <div>
            <div className="text-white text-sm font-medium">Royal Collision Centre</div>
            <div className="text-white/30 text-xs">Estimates Dashboard</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchEstimates}
            disabled={loading}
            className="whitespace-nowrap flex items-center gap-2 px-4 py-2 text-xs text-yellow-400 border border-yellow-700/40 rounded-lg hover:bg-yellow-900/20 transition-colors cursor-pointer disabled:opacity-50"
          >
            <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`}></i>
            Refresh
          </button>
          <button
            onClick={() => setAuthenticated(false)}
            className="whitespace-nowrap flex items-center gap-2 px-4 py-2 text-xs text-white/40 border border-white/10 rounded-lg hover:bg-white/5 transition-colors cursor-pointer"
          >
            <i className="ri-logout-box-line"></i>
            Logout
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 md:px-10 py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {(['all', 'pending', 'reviewed', 'contacted'] as const).map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`whitespace-nowrap text-left p-4 rounded-xl border transition-all duration-200 cursor-pointer ${
                filter === s
                  ? 'bg-yellow-900/20 border-yellow-600/50'
                  : 'bg-[#111111] border-yellow-700/20 hover:border-yellow-700/40'
              }`}
            >
              <div className="text-2xl font-light text-white mb-1">{counts[s]}</div>
              <div className="text-white/40 text-xs capitalize">{s === 'all' ? 'Total' : s}</div>
            </button>
          ))}
        </div>

        {fetchError && (
          <div className="bg-red-900/20 border border-red-700/40 rounded-lg px-4 py-3 text-red-400 text-xs mb-6">
            {fetchError}
          </div>
        )}

        {/* Estimates List */}
        <div className="space-y-3">
          {filtered.length === 0 ? (
            <div className="text-center py-20 text-white/30">
              <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4">
                <i className="ri-inbox-line text-3xl"></i>
              </div>
              <p className="text-sm">No estimates yet</p>
            </div>
          ) : (
            filtered.map((estimate) => (
              <EstimateCard key={estimate.id} estimate={estimate} />
            ))
          )}
        </div>
      </div>
    </div>
  );
}
